import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { EntityAutocomplete } from "@/components/EntityAutocomplete";
import { CostosEditor, newCost } from "@/pages/LoadForm";
import { ConfidenceField, DocumentPreview, MatchScoreNote } from "@/components/DocumentPreview";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UploadCloud, FileText, CheckCircle2, XCircle, AlertTriangle, ArrowLeft, Loader2, RefreshCw } from "lucide-react";

const ACCEPTED = ".pdf,.jpg,.jpeg,.png,.heic,.heif";

const TRIP_OPTIONS = [
  { value: "nacional_mx", label: "Nacional MX" },
  { value: "nacional_us", label: "Nacional US" },
  { value: "transfronterizo", label: "Transfronterizo" },
];

function tipoViajeFromPaises(origenPais, destinoPais) {
  if (origenPais === "MX" && destinoPais === "MX") return "nacional_mx";
  if (origenPais === "US" && destinoPais === "US") return "nacional_us";
  return "transfronterizo";
}

function fieldVal(campos, key) { return campos?.[key]?.valor ?? null; }
function fieldConf(campos, key) { return campos?.[key]?.confianza || "baja"; }
const emptyLeg = (moneda) => ({ carrier_id: "", carrier_label: "", monto_cobro: "", moneda_cobro: moneda, costos: [] });

export default function DocumentUploadLoad() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [queue, setQueue] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [activeDocId, setActiveDocId] = useState(null);

  const pendingCount = queue.filter((q) => q.status === "extraido" || q.status === "error").length;

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList);
    if (files.length === 0) return;
    for (const file of files) {
      const tempId = `${file.name}-${Date.now()}-${Math.random()}`;
      setQueue((q) => [...q, { tempId, filename: file.name, status: "subiendo", doc: null }]);
      try {
        const fd = new FormData();
        fd.append("file", file);
        setQueue((q) => q.map((it) => (it.tempId === tempId ? { ...it, status: "extrayendo" } : it)));
        const { data } = await api.post("/documents/upload-load", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setQueue((q) => q.map((it) => (it.tempId === tempId
          ? { ...it, status: data.estado_procesamiento === "error" ? "error" : "extraido", doc: data, errorMsg: data.error_mensaje }
          : it)));
      } catch (e) {
        setQueue((q) => q.map((it) => (it.tempId === tempId
          ? { ...it, status: "error", errorMsg: formatApiErrorDetail(e.response?.data?.detail) }
          : it)));
      }
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const reprocesar = async (tempId, docId) => {
    setQueue((q) => q.map((it) => (it.tempId === tempId ? { ...it, status: "extrayendo" } : it)));
    try {
      const { data } = await api.post(`/documents/${docId}/reprocesar`);
      setQueue((q) => q.map((it) => (it.tempId === tempId
        ? { ...it, status: data.estado_procesamiento === "error" ? "error" : "extraido", doc: data, errorMsg: data.error_mensaje }
        : it)));
    } catch (e) {
      setQueue((q) => q.map((it) => (it.tempId === tempId
        ? { ...it, status: "error", errorMsg: formatApiErrorDetail(e.response?.data?.detail) }
        : it)));
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const activeItem = queue.find((q) => q.doc?.id === activeDocId);

  if (activeItem) {
    return (
      <ConfirmLoadPanel
        item={activeItem}
        onDone={() => {
          setQueue((q) => q.map((it) => (it.doc?.id === activeDocId ? { ...it, status: "confirmado" } : it)));
          setActiveDocId(null);
        }}
        onBack={() => setActiveDocId(null)}
      />
    );
  }

  return (
    <div>
      <PageHeader
        title="Nuevo load desde documento"
        subtitle="Sube la Carta de Instrucciones (Rate Confirmation) y la IA extraera los datos para tu revision"
        actions={
          <Button variant="outline" onClick={() => navigate("/loads")} className="gap-2" data-testid="docload-back-to-loads">
            <ArrowLeft className="h-4 w-4" /> Volver a Loads
          </Button>
        }
      />

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileRef.current?.click()}
        data-testid="doc-load-dropzone"
        className={`mb-6 flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-10 text-center transition-colors ${
          dragOver ? "border-primary bg-green-50" : "border-border bg-white hover:bg-slate-50"
        }`}
      >
        <UploadCloud className="h-8 w-8 text-primary" />
        <p className="text-sm font-medium text-foreground">Arrastra y suelta archivos aqui, o haz clic para seleccionar</p>
        <p className="text-xs text-muted-foreground">PDF, JPG, PNG o HEIC - maximo 10 MB por archivo. Puedes subir varios a la vez.</p>
        <input ref={fileRef} type="file" multiple accept={ACCEPTED} className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid="doc-load-file-input" />
      </div>

      {queue.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between font-heading text-base">
              <span>Cola de procesamiento</span>
              {pendingCount > 0 && <span className="text-sm font-normal text-amber-700" data-testid="doc-load-pending-count">{pendingCount} pendiente(s) de confirmar</span>}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {queue.map((item, i) => (
              <div key={item.tempId} className="flex items-center justify-between rounded-md border border-border p-3" data-testid={`doc-load-queue-item-${i}`}>
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{item.filename}</p>
                    <StatusLabel status={item.status} errorMsg={item.errorMsg} />
                  </div>
                </div>
                <div className="flex gap-2">
                  {item.status === "error" && item.doc && (
                    <Button size="sm" variant="outline" className="gap-1" onClick={() => reprocesar(item.tempId, item.doc.id)} data-testid={`doc-load-queue-reprocess-${i}`}>
                      <RefreshCw className="h-3.5 w-3.5" /> Reprocesar
                    </Button>
                  )}
                  {(item.status === "extraido" || item.status === "error") && item.doc && (
                    <Button size="sm" onClick={() => setActiveDocId(item.doc.id)} data-testid={`doc-load-queue-confirm-${i}`}>
                      {item.status === "error" ? "Completar manualmente" : "Revisar y confirmar"}
                    </Button>
                  )}
                </div>
                {item.status === "confirmado" && <span className="text-sm font-semibold text-green-700">Load creado</span>}
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function StatusLabel({ status, errorMsg }) {
  const MAP = {
    subiendo: { icon: Loader2, cls: "text-muted-foreground animate-spin", text: "Subiendo..." },
    extrayendo: { icon: Loader2, cls: "text-primary animate-spin", text: "Extrayendo con IA..." },
    extraido: { icon: CheckCircle2, cls: "text-green-600", text: "Listo para revisar" },
    confirmado: { icon: CheckCircle2, cls: "text-green-600", text: "Load creado" },
    error: { icon: XCircle, cls: "text-red-600", text: errorMsg || "No se pudo procesar automaticamente - carga manual disponible" },
  };
  const m = MAP[status] || MAP.extraido;
  const Icon = m.icon;
  return <p className={`flex items-center gap-1 text-xs ${m.cls}`}><Icon className="h-3 w-3" /> {m.text}</p>;
}

function ConfirmLoadPanel({ item, onDone, onBack }) {
  const doc = item.doc;
  const campos = doc.datos_extraidos || {};
  const initialOrigenPais = fieldVal(campos, "origen_pais") || "MX";
  const initialDestinoPais = fieldVal(campos, "destino_pais") || "US";
  const initialMonedaCobro = fieldVal(campos, "moneda_cobro") || "USD";

  const carrierIdInit = doc.sugerido_carrier?.id || null;
  const carrierLabelInit = doc.sugerido_carrier ? `${doc.sugerido_carrier.codigo} - ${doc.sugerido_carrier.razon_social}` : "";
  const costoCarrierVal = fieldVal(campos, "costo_carrier");
  const monedaCostoExtraida = fieldVal(campos, "moneda_costo_carrier");
  const monedaCostoCarrier = monedaCostoExtraida || doc.sugerido_carrier?.moneda_default || "USD";
  const monedaCostoInferida = !monedaCostoExtraida && costoCarrierVal != null;

  // Si el documento es de un viaje transfronterizo, no sabemos a cuál tramo pertenecen el
  // carrier/costo extraídos — inferimos por el país del carrier sugerido (o por el origen si no
  // hay carrier). El otro tramo queda vacío para completar a mano.
  const carrierPaisFromMatch = doc.sugerido_carrier?.pais;
  const extractedLegIsMx = carrierPaisFromMatch ? carrierPaisFromMatch === "MX" : initialOrigenPais === "MX";
  const extractedCostRow = costoCarrierVal != null ? [{
    ...newCost(monedaCostoCarrier),
    tipo_costo: extractedLegIsMx ? "carrier_mx" : "carrier_us",
    carrier_id: carrierIdInit, carrier_label: carrierLabelInit,
    monto: costoCarrierVal, moneda: monedaCostoCarrier,
  }] : [];
  const extractedLeg = { carrier_id: carrierIdInit || "", carrier_label: carrierLabelInit,
    monto_cobro: fieldVal(campos, "monto_acordado") ?? "", moneda_cobro: initialMonedaCobro, costos: extractedCostRow };

  const [form, setForm] = useState({
    tipo_viaje: tipoViajeFromPaises(initialOrigenPais, initialDestinoPais),
    client_id: doc.sugerido_client?.id || "",
    client_label: doc.sugerido_client ? `${doc.sugerido_client.codigo} - ${doc.sugerido_client.razon_social}` : "",
    referencia_cliente: fieldVal(campos, "referencia_cliente") || fieldVal(campos, "numero_load_cliente") || "",
    origen_ciudad: fieldVal(campos, "origen_ciudad") || "", origen_estado: fieldVal(campos, "origen_estado") || "", origen_pais: initialOrigenPais,
    destino_ciudad: fieldVal(campos, "destino_ciudad") || "", destino_estado: fieldVal(campos, "destino_estado") || "", destino_pais: initialDestinoPais,
    fecha_pickup: fieldVal(campos, "fecha_pickup") || "", fecha_delivery: fieldVal(campos, "fecha_delivery") || "",
    notas: fieldVal(campos, "notas_adicionales") || "",
    carrier_id: carrierIdInit || "", carrier_label: carrierLabelInit,
    monto_cobro: fieldVal(campos, "monto_acordado") ?? "", moneda_cobro: initialMonedaCobro,
    costos: extractedCostRow,
    tramo_mx: extractedLegIsMx ? { ...extractedLeg } : emptyLeg("MXN"),
    tramo_us: !extractedLegIsMx ? { ...extractedLeg } : emptyLeg("USD"),
  });

  const [saving, setSaving] = useState(false);
  const [dup, setDup] = useState(null);
  const [confirmDifuso, setConfirmDifuso] = useState(false);
  const [confirmCobroDoble, setConfirmCobroDoble] = useState(false);
  const [tarifa, setTarifa] = useState(null);
  const [confirmFueraTarifa, setConfirmFueraTarifa] = useState(false);
  const [tipoCambio, setTipoCambio] = useState(17.5);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setTipoCambio(data.tipo_cambio_usd_mxn || 17.5)).catch(() => {});
  }, []);

  const isXB = form.tipo_viaje === "transfronterizo";

  const fetchTarifa = useCallback(async (overrides = {}) => {
    const origen = overrides.origen_ciudad ?? form.origen_ciudad;
    const destino = overrides.destino_ciudad ?? form.destino_ciudad;
    const clientId = overrides.client_id ?? form.client_id;
    if (!origen || !destino) { setTarifa(null); return; }
    try {
      const { data } = await api.get("/rate-cards/match", { params: { origen_ciudad: origen, destino_ciudad: destino, client_id: clientId || "" } });
      setTarifa(data);
      setConfirmFueraTarifa(false);
    } catch {
      setTarifa(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.origen_ciudad, form.destino_ciudad, form.client_id]);

  useEffect(() => {
    fetchTarifa({ origen_ciudad: form.origen_ciudad, destino_ciudad: form.destino_ciudad, client_id: form.client_id });
    checkDuplicate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkDuplicate = async () => {
    if (!form.client_id || !form.referencia_cliente || !form.origen_ciudad || !form.destino_ciudad || !form.fecha_pickup) return;
    try {
      const { data } = await api.get("/loads/check-duplicate", {
        params: {
          client_id: form.client_id, referencia_cliente: form.referencia_cliente,
          origen_ciudad: form.origen_ciudad, destino_ciudad: form.destino_ciudad, fecha_pickup: form.fecha_pickup,
        },
      });
      setDup(data.exacto || data.difuso ? data : null);
      setConfirmDifuso(false);
    } catch {
      /* silent - final validation happens server side on submit */
    }
  };

  const toUsd = (monto, moneda) => (moneda === "MXN" ? parseFloat(monto || 0) / tipoCambio : parseFloat(monto || 0));

  // No transfronterizo: cálculo simple sobre un solo tramo.
  const costosUsd = form.costos.reduce((s, c) => s + toUsd(c.monto, c.moneda), 0);
  const montoCobroUsd = toUsd(form.monto_cobro, form.moneda_cobro);
  const margenResultante = montoCobroUsd ? Math.round(((montoCobroUsd - costosUsd) / montoCobroUsd) * 10000) / 100 : 0;
  const excedeTechoMain = tarifa ? costosUsd > toUsd(tarifa.costo_techo, tarifa.moneda) : false;

  // Transfronterizo: el techo/margen se evalúa contra la SUMA consolidada de ambos tramos,
  // igual que en el backend (_tarifa_check_consolidado) — no por tramo.
  const costosMxUsd = form.tramo_mx.costos.reduce((s, c) => s + toUsd(c.monto, c.moneda), 0);
  const costosUsUsd = form.tramo_us.costos.reduce((s, c) => s + toUsd(c.monto, c.moneda), 0);
  const costosTotalXbUsd = costosMxUsd + costosUsUsd;
  const montoMxUsd = toUsd(form.tramo_mx.monto_cobro, form.tramo_mx.moneda_cobro);
  const montoUsUsd = toUsd(form.tramo_us.monto_cobro, form.tramo_us.moneda_cobro);
  const montoTotalXbUsd = montoMxUsd + montoUsUsd;
  const margenXb = montoTotalXbUsd ? Math.round(((montoTotalXbUsd - costosTotalXbUsd) / montoTotalXbUsd) * 10000) / 100 : 0;
  const excedeTechoXb = tarifa ? costosTotalXbUsd > toUsd(tarifa.costo_techo, tarifa.moneda) : false;

  const excedeTecho = isXB ? excedeTechoXb : excedeTechoMain;
  const margenActivo = isXB ? margenXb : margenResultante;
  const margenFueraDeRango = margenActivo < -50 || margenActivo > 90;
  const cobroDobleDetectado = isXB && Number(form.tramo_mx.monto_cobro || 0) > 0 && Number(form.tramo_us.monto_cobro || 0) > 0;

  // Confianza derivada de tipo de viaje: combina la confianza de origen_pais y destino_pais.
  const origenPaisConf = fieldConf(campos, "origen_pais");
  const destinoPaisConf = fieldConf(campos, "destino_pais");
  const tipoViajeConf = (origenPaisConf === "baja" || destinoPaisConf === "baja") ? "baja"
    : (origenPaisConf === "media" || destinoPaisConf === "media") ? "media" : "alta";
  const tipoViajeValue = (fieldVal(campos, "origen_pais") && fieldVal(campos, "destino_pais")) ? "ok" : null;

  const buildPayload = () => {
    const cost = (c) => ({ tipo_costo: c.tipo_costo, carrier_id: c.carrier_id || null, concepto: c.concepto || "",
      monto: parseFloat(c.monto || 0), moneda: c.moneda, estado_pago: c.estado_pago });
    const base = {
      tipo_viaje: form.tipo_viaje, client_id: form.client_id, referencia_cliente: form.referencia_cliente,
      origen_ciudad: form.origen_ciudad, origen_estado: form.origen_estado, origen_pais: form.origen_pais,
      destino_ciudad: form.destino_ciudad, destino_estado: form.destino_estado, destino_pais: form.destino_pais,
      fecha_pickup: form.fecha_pickup, fecha_delivery: form.fecha_delivery, notas: form.notas,
      confirmar_duplicado_difuso: confirmDifuso, confirmar_cobro_doble: confirmCobroDoble,
      confirmar_fuera_tarifa: confirmFueraTarifa,
    };
    if (isXB) {
      return {
        ...base,
        tramo_mx: { carrier_id: form.tramo_mx.carrier_id || null, monto_cobro: parseFloat(form.tramo_mx.monto_cobro || 0), moneda_cobro: form.tramo_mx.moneda_cobro, costos: form.tramo_mx.costos.map(cost) },
        tramo_us: { carrier_id: form.tramo_us.carrier_id || null, monto_cobro: parseFloat(form.tramo_us.monto_cobro || 0), moneda_cobro: form.tramo_us.moneda_cobro, costos: form.tramo_us.costos.map(cost) },
      };
    }
    return {
      ...base,
      carrier_id: form.carrier_id || null, monto_cobro: parseFloat(form.monto_cobro || 0), moneda_cobro: form.moneda_cobro,
      costos: form.costos.map(cost),
    };
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/documents/${doc.id}/confirm-load`, buildPayload());
      toast.success(`Load ${data.numero_load} creado`);
      onDone();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.code === "duplicado_exacto") { setDup({ exacto: detail, difuso: null }); toast.error(detail.message); }
      else if (detail?.code === "posible_duplicado") { setDup({ exacto: null, difuso: detail }); toast.warning(detail.message); }
      else if (detail?.code === "cobro_doble") toast.warning(detail.message);
      else if (detail?.code === "fuera_de_tarifa") toast.warning(detail.message);
      else toast.error(formatApiErrorDetail(detail));
    } finally {
      setSaving(false);
    }
  };

  const requiredOk = form.client_id && form.referencia_cliente && form.origen_ciudad && form.destino_ciudad
    && form.fecha_pickup && form.fecha_delivery;
  const needsTarifaConfirm = excedeTecho && !confirmFueraTarifa;
  const blockedByExactDup = !!dup?.exacto;
  const needsDifusoConfirm = !!dup?.difuso && !confirmDifuso;
  const needsCobroDobleConfirm = cobroDobleDetectado && !confirmCobroDoble;
  const canSave = requiredOk && !blockedByExactDup && !needsDifusoConfirm && !needsCobroDobleConfirm && !needsTarifaConfirm && !saving;

  return (
    <div>
      <PageHeader
        title="Confirmar load desde documento"
        subtitle={`Documento: ${doc.archivo_nombre} - revision humana obligatoria antes de guardar`}
        actions={
          <Button variant="outline" onClick={onBack} className="gap-2" data-testid="docload-back-to-queue">
            <ArrowLeft className="h-4 w-4" /> Volver a la cola
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="h-[70vh] lg:h-[80vh]">
          <DocumentPreview docId={doc.id} mimeType={doc.archivo_tipo} testId="docload-preview" />
        </div>

        <div className="space-y-4 lg:h-[80vh] lg:overflow-y-auto lg:pr-2">
          <Card>
            <CardHeader><CardTitle className="font-heading text-base">Tipo de viaje</CardTitle></CardHeader>
            <CardContent>
              <ConfidenceField confianza={tipoViajeConf} value={tipoViajeValue} testId="docload-conf-tipo-viaje">
                <div className="grid grid-cols-3 gap-2">
                  {TRIP_OPTIONS.map((opt) => (
                    <button key={opt.value} type="button" onClick={() => setForm({ ...form, tipo_viaje: opt.value })}
                      data-testid={`docload-tipo-viaje-${opt.value}`}
                      className={`rounded-md border p-2 text-xs font-medium transition-colors ${
                        form.tipo_viaje === opt.value ? "border-primary bg-green-50" : "border-border hover:bg-slate-50"}`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </ConfidenceField>
              {isXB && (
                <p className="mt-2 flex items-center gap-1 text-xs text-muted-foreground" data-testid="docload-xb-warning">
                  <AlertTriangle className="h-3.5 w-3.5" /> Viaje transfronterizo: completa los datos de ambos tramos (MX y US) más abajo.
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="font-heading text-base">Identificacion</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs text-muted-foreground">Cliente *</Label>
                <ConfidenceField confianza={fieldConf(campos, "nombre_cliente")} value={fieldVal(campos, "nombre_cliente")} testId="docload-conf-cliente">
                  <div className="mt-1">
                    <EntityAutocomplete type="clients" value={form.client_id} label={form.client_label}
                      onSelect={(c) => { setForm({ ...form, client_id: c.id, client_label: `${c.codigo} - ${c.razon_social}` }); fetchTarifa({ client_id: c.id }); }}
                      testId="docload-client-autocomplete" />
                  </div>
                </ConfidenceField>
                <MatchScoreNote match={doc.sugerido_client} testId="docload-client-match-score" />
              </div>
              <ConfidenceField confianza={fieldConf(campos, "referencia_cliente")} value={fieldVal(campos, "referencia_cliente") || fieldVal(campos, "numero_load_cliente")} testId="docload-conf-referencia">
                <Label className="text-xs text-muted-foreground">Referencia del cliente *</Label>
                <Input className="mt-1" value={form.referencia_cliente} onChange={(e) => setForm({ ...form, referencia_cliente: e.target.value })} onBlur={checkDuplicate} data-testid="docload-referencia-cliente" />
              </ConfidenceField>
              {fieldVal(campos, "numero_load_cliente") && (
                <p className="text-xs text-muted-foreground">
                  Numero de load del cliente (segun documento): <span className="font-mono">{fieldVal(campos, "numero_load_cliente")}</span>
                </p>
              )}

              {dup?.exacto && (
                <div className="rounded-md border border-red-300 bg-red-50 p-3" data-testid="docload-exact-duplicate-banner">
                  <div className="flex items-center gap-2 font-medium text-red-800"><XCircle className="h-4 w-4" /> Load duplicado</div>
                  <p className="mt-1 text-sm text-red-800">{dup.exacto.message}</p>
                </div>
              )}
              {!dup?.exacto && dup?.difuso && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3" data-testid="docload-fuzzy-duplicate-banner">
                  <div className="flex items-center gap-2 font-medium text-amber-800"><AlertTriangle className="h-4 w-4" /> Posible duplicado</div>
                  <p className="mt-1 text-sm text-amber-800">{dup.difuso.message} (load {dup.difuso.numero_load})</p>
                  <label className="mt-2 flex items-center gap-2 text-sm text-amber-800">
                    <input type="checkbox" checked={confirmDifuso} onChange={(e) => setConfirmDifuso(e.target.checked)} data-testid="docload-confirm-difuso-checkbox" /> Confirmo que es un viaje distinto
                  </label>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="font-heading text-base">Ruta y fechas</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <ConfidenceField confianza={fieldConf(campos, "origen_ciudad")} value={fieldVal(campos, "origen_ciudad")} testId="docload-conf-origen">
                <Label className="text-xs text-muted-foreground">Origen ciudad *</Label>
                <Input className="mt-1" value={form.origen_ciudad} onChange={(e) => setForm({ ...form, origen_ciudad: e.target.value })} onBlur={() => { checkDuplicate(); fetchTarifa(); }} data-testid="docload-origen-ciudad" />
              </ConfidenceField>
              <ConfidenceField confianza={fieldConf(campos, "destino_ciudad")} value={fieldVal(campos, "destino_ciudad")} testId="docload-conf-destino">
                <Label className="text-xs text-muted-foreground">Destino ciudad *</Label>
                <Input className="mt-1" value={form.destino_ciudad} onChange={(e) => setForm({ ...form, destino_ciudad: e.target.value })} onBlur={() => { checkDuplicate(); fetchTarifa(); }} data-testid="docload-destino-ciudad" />
              </ConfidenceField>
              <ConfidenceField confianza={fieldConf(campos, "origen_estado")} value={fieldVal(campos, "origen_estado")} testId="docload-conf-origen-estado">
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Estado origen" value={form.origen_estado} onChange={(e) => setForm({ ...form, origen_estado: e.target.value })} data-testid="docload-origen-estado" />
                  <ConfidenceField confianza={fieldConf(campos, "origen_pais")} value={fieldVal(campos, "origen_pais")} testId="docload-conf-origen-pais">
                    <Select value={form.origen_pais} onValueChange={(v) => setForm({ ...form, origen_pais: v })}>
                      <SelectTrigger data-testid="docload-origen-pais"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="MX">MX</SelectItem><SelectItem value="US">US</SelectItem></SelectContent>
                    </Select>
                  </ConfidenceField>
                </div>
              </ConfidenceField>
              <ConfidenceField confianza={fieldConf(campos, "destino_estado")} value={fieldVal(campos, "destino_estado")} testId="docload-conf-destino-estado">
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Estado destino" value={form.destino_estado} onChange={(e) => setForm({ ...form, destino_estado: e.target.value })} data-testid="docload-destino-estado" />
                  <ConfidenceField confianza={fieldConf(campos, "destino_pais")} value={fieldVal(campos, "destino_pais")} testId="docload-conf-destino-pais">
                    <Select value={form.destino_pais} onValueChange={(v) => setForm({ ...form, destino_pais: v })}>
                      <SelectTrigger data-testid="docload-destino-pais"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="MX">MX</SelectItem><SelectItem value="US">US</SelectItem></SelectContent>
                    </Select>
                  </ConfidenceField>
                </div>
              </ConfidenceField>
              <ConfidenceField confianza={fieldConf(campos, "fecha_pickup")} value={fieldVal(campos, "fecha_pickup")} testId="docload-conf-pickup">
                <Label className="text-xs text-muted-foreground">Fecha pickup *</Label>
                <Input type="date" className="mt-1" value={form.fecha_pickup} onChange={(e) => setForm({ ...form, fecha_pickup: e.target.value })} onBlur={checkDuplicate} data-testid="docload-fecha-pickup" />
              </ConfidenceField>
              <ConfidenceField confianza={fieldConf(campos, "fecha_delivery")} value={fieldVal(campos, "fecha_delivery")} testId="docload-conf-delivery">
                <Label className="text-xs text-muted-foreground">Fecha delivery *</Label>
                <Input type="date" className="mt-1" value={form.fecha_delivery} onChange={(e) => setForm({ ...form, fecha_delivery: e.target.value })} data-testid="docload-fecha-delivery" />
              </ConfidenceField>
            </CardContent>
          </Card>

          {tarifa && (
            <Card data-testid="docload-tarifa-panel">
              <CardHeader><CardTitle className="font-heading text-base">Tarifa sugerida</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Costo techo: <span className="font-mono">{tarifa.costo_techo} {tarifa.moneda}</span> - Margen objetivo: {tarifa.margen_objetivo_pct}%
                </p>
                {excedeTecho && (
                  <div className="rounded-md border border-orange-300 bg-orange-50 p-3" data-testid="docload-fuera-tarifa-banner">
                    <div className="flex items-center gap-2 font-medium text-orange-800">
                      <AlertTriangle className="h-4 w-4" /> Supera el techo de costo{isXB ? " (suma de ambos tramos)" : ""}. Margen resultante: {margenActivo}%.
                    </div>
                    <label className="mt-2 flex items-center gap-2 text-sm text-orange-800">
                      <input type="checkbox" checked={confirmFueraTarifa} onChange={(e) => setConfirmFueraTarifa(e.target.checked)} data-testid="docload-confirm-fuera-tarifa-checkbox" /> Confirmo y apruebo continuar fuera de tarifa
                    </label>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {margenFueraDeRango && (
            <div className="rounded-md border border-red-300 bg-red-50 p-3" data-testid="docload-margen-sanity-warning">
              <div className="flex items-center gap-2 font-medium text-red-800"><AlertTriangle className="h-4 w-4" /> Revisá el monto y la moneda</div>
              <p className="mt-1 text-sm text-red-800">
                El margen resultante es {margenActivo}%, fuera de un rango razonable. Esto parece un error de moneda o de monto. Revisá antes de guardar.
              </p>
            </div>
          )}

          {!isXB ? (
            <Card>
              <CardHeader><CardTitle className="font-heading text-base">Cobro y costos</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="sm:col-span-1">
                    <Label className="text-xs text-muted-foreground">Carrier</Label>
                    <div className="mt-1">
                      <EntityAutocomplete type="carriers" value={form.carrier_id} label={form.carrier_label}
                        onSelect={(c) => setForm({ ...form, carrier_id: c.id, carrier_label: `${c.codigo} - ${c.razon_social}` })}
                        testId="docload-carrier-autocomplete" />
                    </div>
                    <MatchScoreNote match={doc.sugerido_carrier} testId="docload-carrier-match-score" />
                  </div>
                  <ConfidenceField confianza={fieldConf(campos, "monto_acordado")} value={fieldVal(campos, "monto_acordado")} testId="docload-conf-monto">
                    <Label className="text-xs text-muted-foreground">Monto cobro *</Label>
                    <Input type="number" className="mt-1" value={form.monto_cobro} onChange={(e) => setForm({ ...form, monto_cobro: e.target.value })} data-testid="docload-monto-cobro" />
                  </ConfidenceField>
                  <ConfidenceField confianza={fieldConf(campos, "moneda_cobro")} value={fieldVal(campos, "moneda_cobro")} testId="docload-conf-moneda">
                    <Label className="text-xs text-muted-foreground">Moneda</Label>
                    <Select value={form.moneda_cobro} onValueChange={(v) => setForm({ ...form, moneda_cobro: v })}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="USD">USD</SelectItem><SelectItem value="MXN">MXN</SelectItem></SelectContent>
                    </Select>
                  </ConfidenceField>
                </div>
                <ConfidenceField confianza={fieldConf(campos, "costo_carrier")} value={fieldVal(campos, "costo_carrier")} testId="docload-conf-costo-carrier">
                  <CostosEditor costos={form.costos} setCostos={(c) => setForm({ ...form, costos: c })} testPrefix="docload" defaultMoneda={form.moneda_cobro} />
                </ConfidenceField>
                {monedaCostoInferida && (
                  <p className="flex items-center gap-1 text-xs text-amber-700" data-testid="docload-moneda-inferida-note">
                    <AlertTriangle className="h-3 w-3" /> Moneda del costo inferida del carrier, verificá.
                  </p>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <LegCard
                title="Tramo México (T)" pais="MX" moneda="MXN" leg={form.tramo_mx}
                setLeg={(l) => setForm({ ...form, tramo_mx: l })}
                hasExtractedData={extractedLegIsMx} monedaCostoInferida={extractedLegIsMx && monedaCostoInferida}
              />
              <LegCard
                title="Tramo Estados Unidos (AL)" pais="US" moneda="USD" leg={form.tramo_us}
                setLeg={(l) => setForm({ ...form, tramo_us: l })}
                hasExtractedData={!extractedLegIsMx} monedaCostoInferida={!extractedLegIsMx && monedaCostoInferida}
              />
            </div>
          )}

          {cobroDobleDetectado && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3" data-testid="docload-cobro-doble-banner">
              <div className="flex items-center gap-2 font-medium text-amber-800">
                <AlertTriangle className="h-4 w-4" /> Posible doble facturación
              </div>
              <p className="mt-1 text-sm text-amber-800">Ambos tramos tienen un cobro registrado al mismo cliente. Verifica que no sea el mismo concepto facturado dos veces.</p>
              <label className="mt-2 flex items-center gap-2 text-sm text-amber-800">
                <input type="checkbox" checked={confirmCobroDoble} onChange={(e) => setConfirmCobroDoble(e.target.checked)} data-testid="docload-confirm-cobro-doble-checkbox" /> Confirmo que ambos cobros son correctos y distintos
              </label>
            </div>
          )}

          <Card>
            <CardHeader><CardTitle className="font-heading text-base">Notas</CardTitle></CardHeader>
            <CardContent>
              <ConfidenceField confianza={fieldConf(campos, "notas_adicionales")} value={fieldVal(campos, "notas_adicionales")} testId="docload-conf-notas">
                <Textarea value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} data-testid="docload-notas" />
              </ConfidenceField>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-2 pb-6">
            <Button variant="outline" onClick={onBack}>Cancelar</Button>
            <Button onClick={save} disabled={!canSave} data-testid="docload-confirm-save-btn">
              {saving ? "Creando load..." : "Confirmar y crear load"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function LegCard({ title, pais, moneda, leg, setLeg, hasExtractedData, monedaCostoInferida }) {
  const testPrefix = pais === "MX" ? "docload-mx" : "docload-us";
  return (
    <Card>
      <CardHeader><CardTitle className="font-heading text-base">{title}</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {!hasExtractedData && (
          <p className="flex items-center gap-1 text-xs text-red-700" data-testid={`${testPrefix}-no-data-warning`}>
            <AlertTriangle className="h-3.5 w-3.5" /> Esta carta solo tiene datos del tramo {pais === "MX" ? "US" : "MX"}. Completá este tramo antes de guardar.
          </p>
        )}
        <div>
          <Label className="text-xs text-muted-foreground">Carrier {pais}{hasExtractedData && <span className="text-primary"> (sugerido)</span>}</Label>
          <div className="mt-1">
            <EntityAutocomplete type="carriers" paisFilter={pais} value={leg.carrier_id} label={leg.carrier_label}
              onSelect={(c) => setLeg({ ...leg, carrier_id: c.id, carrier_label: `${c.codigo} - ${c.razon_social}` })}
              testId={`${testPrefix}-carrier`} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-muted-foreground">Monto cobro</Label>
            <Input type="number" className="mt-1" value={leg.monto_cobro} onChange={(e) => setLeg({ ...leg, monto_cobro: e.target.value })} data-testid={`${testPrefix}-monto`} />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Moneda</Label>
            <Select value={leg.moneda_cobro} onValueChange={(v) => setLeg({ ...leg, moneda_cobro: v })}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="MXN">MXN</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <CostosEditor costos={leg.costos} setCostos={(c) => setLeg({ ...leg, costos: c })} testPrefix={testPrefix} defaultMoneda={moneda} />
        {monedaCostoInferida && (
          <p className="flex items-center gap-1 text-xs text-amber-700" data-testid={`${testPrefix}-moneda-inferida-note`}>
            <AlertTriangle className="h-3 w-3" /> Moneda del costo inferida del carrier, verificá.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

